import { create } from 'zustand';

export type PlanType = 'free' | 'standard' | 'premium';
export type PlanSelectionSource = 'landing' | 'pachete' | 'registration' | 'payment' | 'unknown';

const STORAGE_KEY = 'selected_plan';

type PlanSelectionSnapshot = {
  plan: PlanType;
  source: PlanSelectionSource;
  timestamp: number;
};

const persistSelection = (snapshot: PlanSelectionSnapshot | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!snapshot) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
};

const hydrateSelection = (): PlanSelectionSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as PlanSelectionSnapshot;
    if (parsed?.plan && ['free', 'standard', 'premium'].includes(parsed.plan)) {
      return parsed;
    }
  } catch {
    // ignore parsing errors
  }

  sessionStorage.removeItem(STORAGE_KEY);
  return null;
};

interface PlanSelectionStore {
  selectedPlan: PlanType | null;
  selectionSource: PlanSelectionSource | null;
  setSelectedPlan: (plan: PlanType, source?: PlanSelectionSource) => void;
  clearSelectedPlan: () => void;
}

const initialSnapshot = hydrateSelection();

export const usePlanSelectionStore = create<PlanSelectionStore>((set) => ({
  selectedPlan: initialSnapshot?.plan ?? null,
  selectionSource: initialSnapshot?.source ?? null,
  setSelectedPlan: (plan: PlanType, source: PlanSelectionSource = 'registration') => {
    const snapshot: PlanSelectionSnapshot = {
      plan,
      source,
      timestamp: Date.now(),
    };
    persistSelection(snapshot);
    set({ selectedPlan: plan, selectionSource: source });
  },
  clearSelectedPlan: () => {
    persistSelection(null);
    set({ selectedPlan: null, selectionSource: null });
  },
}));

